import { AppDataSchema, type AppData, type LoadedData } from '../domain';
import { demoData } from './demo-data';

const CACHE_KEY = 'mallang-note:validated-bundle:v1';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const hasFirestoreConfig = (): boolean =>
  Object.values(firebaseConfig).every((value) => typeof value === 'string' && value.length > 0);

const readCache = (): AppData | null => {
  try {
    const value = localStorage.getItem(CACHE_KEY);
    if (!value) return null;
    return AppDataSchema.parse(JSON.parse(value));
  } catch {
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
};

const writeCache = (data: AppData): void => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // A successful network read remains usable even when storage is unavailable.
  }
};

const readFirestoreBundle = async (): Promise<AppData> => {
  const [{ initializeApp, getApps }, { doc, getDoc, getFirestore }] = await Promise.all([
    import('firebase/app'),
    import('firebase/firestore'),
  ]);

  const app = getApps()[0] ?? initializeApp(firebaseConfig);
  const db = getFirestore(app, 'default');
  const currentSnapshot = await getDoc(doc(db, 'publicMeta', 'current'));

  if (!currentSnapshot.exists()) throw new Error('CURRENT_VERSION_MISSING');

  const current = currentSnapshot.data();
  const bundleVersion = current.activeBundleVersion ?? current.bundleVersion;
  if (typeof bundleVersion !== 'string' || bundleVersion.length === 0) {
    throw new Error('ACTIVE_BUNDLE_INVALID');
  }

  const bundleSnapshot = await getDoc(doc(db, 'publicBundles', bundleVersion));
  if (!bundleSnapshot.exists()) throw new Error('ACTIVE_BUNDLE_MISSING');

  const raw = bundleSnapshot.data();
  const payload = typeof raw.payloadJson === 'string'
    ? JSON.parse(raw.payloadJson) as unknown
    : raw.payload ?? raw;
  return AppDataSchema.parse(payload);
};

export const loadAppData = async (): Promise<LoadedData> => {
  if (!hasFirestoreConfig()) {
    return { data: demoData, origin: 'sample', warning: null };
  }

  try {
    const data = await readFirestoreBundle();
    writeCache(data);
    return { data, origin: 'firestore', warning: null };
  } catch {
    const cached = readCache();
    if (cached) {
      return {
        data: cached,
        origin: 'cache',
        warning: '네트워크 연결이 원활하지 않아 마지막으로 확인한 데이터를 보여드려요.',
      };
    }

    return {
      data: demoData,
      origin: 'sample',
      warning: '운영 데이터를 불러오지 못해 화면 검증용 샘플 데이터를 보여드려요.',
    };
  }
};
